import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApi, errMsg } from "@/lib/api";
import { COLLECTION_LABELS } from "@/lib/sp";
import { PageHeader } from "@/components/PageHeader";

export default function TrashPage() {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [purge, setPurge] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api("get", "/sp-trash").then(setItems).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  const run = async (b, fn, msg) => {
    setBusy(b);
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-8" data-testid="trash-page">
      <PageHeader title="Corbeille" subtitle="Éléments supprimés de l'application Sehatic-Pedia. Restaurez-les à l'identique, ou videz-les définitivement." />
      <div className="bg-white border border-stone-200 rounded-lg divide-y divide-stone-100" data-testid="trash-list">
        {items === null && <p className="p-6 text-sm text-stone-500">Chargement…</p>}
        {items?.length === 0 && <p className="p-8 text-center text-sm text-stone-500" data-testid="trash-empty">La corbeille est vide.</p>}
        {items?.map((t) => (
          <div key={t.batch} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" data-testid={`trash-row-${t.batch}`}>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-stone-900">{t.label}</p>
              <p className="text-xs text-stone-500">{COLLECTION_LABELS[t.collection] || t.collection}{t.count > 1 ? ` + ${t.count - 1} donnée(s) liée(s)` : ""} · supprimé le {new Date(t.deleted_at).toLocaleString("fr-FR")} par {t.deleted_by}</p>
            </div>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run(t.batch, () => api("post", `/sp-trash/${t.batch}/restore`), "Élément restauré dans Sehatic-Pedia")} data-testid={`trash-restore-${t.batch}`}><RotateCcw className="h-4 w-4 mr-1.5" /> Restaurer</Button>
            <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" disabled={!!busy} onClick={() => setPurge(t)} data-testid={`trash-purge-${t.batch}`}><Trash2 className="h-4 w-4 mr-1.5" /> Effacer</Button>
          </div>
        ))}
      </div>
      <AlertDialog open={!!purge} onOpenChange={(o) => !o && setPurge(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Effacer définitivement ?</AlertDialogTitle><AlertDialogDescription>« {purge?.label} » ne pourra plus être restauré.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-purge-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-700 hover:bg-red-800" onClick={() => run(purge.batch, () => api("delete", `/sp-trash/${purge.batch}`), "Élément effacé définitivement")} data-testid="trash-purge-confirm">Effacer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
